import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import KODIAK_FARM_ABI from 'abis/KodiakFarm.json'
import { ButtonPrimary } from 'components/Button'
import CurrencyLogo from 'components/CurrencyLogo'
import { useTokens } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import { useReadContractMultiple } from 'hooks/useReadContractMultiple'
import { useWriteContract } from 'hooks/useTransaction'
import useWaitForTransactionReceipt from 'hooks/useWaitForTransactionReceipt'
import { useContext, useMemo, useState } from 'react'
import { Text } from 'rebass'
import { useTransactionAdder } from 'state/transactions/hooks'
import { TransactionType } from 'state/transactions/types'
import { ThemeContext } from 'styled-components'
import { ThemedText } from 'theme'
import { calculateGasMargin } from 'utils/calculateGasMargin'
import { Abi, Address, Hash } from 'viem'

import { useFarm } from './context/FarmContext'
import { CurrencyFlexBetween, FlexRowCenter, InnerCardFlex } from './styleds'

const EarnedCard = () => {
  const theme = useContext(ThemeContext)
  const farm = useFarm()
  const { account, wagmiPublicClient } = useConnection()
  const { writeContractAsync } = useWriteContract()
  const addTransaction = useTransactionAdder()
  const waitForTransactionReceipt = useWaitForTransactionReceipt()
  const [isClaiming, setIsClaiming] = useState(false)

  const contracts = useMemo(() => {
    if (!farm.address || !account) return []
    return [
      {
        address: farm.address as Address,
        abi: KODIAK_FARM_ABI as Abi,
        functionName: 'earned',
        args: [account as Address],
      },
      {
        address: farm.address as Address,
        abi: KODIAK_FARM_ABI as Abi,
        functionName: 'getAllRewardTokens',
      },
    ]
  }, [farm.address, account])

  const { data: results, isLoading } = useReadContractMultiple({
    contracts,
    query: {
      enabled: contracts.length > 0,
    },
  })

  const earnedResult = results?.[0]
  const rewardTokensResult = results?.[1]

  const rewardTokensAddresses = useMemo(() => (rewardTokensResult?.result as string[]) || [], [rewardTokensResult])
  const rewardTokens = useTokens(rewardTokensAddresses)

  const earned = useMemo(() => {
    if (isLoading || !earnedResult?.result) return undefined

    try {
      const raws = earnedResult.result as bigint[]

      return raws
        .map((raw, index) => {
          const token = rewardTokens?.[index]
          if (!token) return undefined
          return {
            token,
            amount: CurrencyAmount.fromRawAmount(token, raw.toString()),
          }
        })
        .filter((item): item is { token: Token; amount: CurrencyAmount<Token> } => item !== undefined)
    } catch (error) {
      console.error('Error processing earned rewards:', error)
      return undefined
    }
  }, [earnedResult, rewardTokens, isLoading])

  return (
    <>
      <ThemedText.DeprecatedBody fontSize="14px" fontWeight="400" lineHeight="14px" color={theme.textPrimary}>
        Earned
      </ThemedText.DeprecatedBody>
      {earned?.map((item, index) => (
        <InnerCardFlex key={index}>
          <CurrencyFlexBetween>
            <FlexRowCenter key={`${index}-${item.token?.address}`}>
              <CurrencyLogo currency={item.token} size="20px" />
              <ThemedText.DeprecatedBody fontSize="14px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
                {item.token?.symbol}
              </ThemedText.DeprecatedBody>
            </FlexRowCenter>
            <ThemedText.DeprecatedBody fontSize="14px" fontWeight="500" lineHeight="20px" color={theme.textPrimary}>
              {item.amount?.toSignificant()}
            </ThemedText.DeprecatedBody>
          </CurrencyFlexBetween>
        </InnerCardFlex>
      ))}
      <ButtonPrimary
        onClick={async () => {
          if (!farm.address || !account || !wagmiPublicClient || isClaiming) return
          setIsClaiming(true)
          try {
            const tx = {
              address: farm.address as Address,
              abi: KODIAK_FARM_ABI as Abi,
              functionName: 'getReward',
            }
            const gas = await wagmiPublicClient.estimateContractGas({ ...tx, account })
            const gasLimit = calculateGasMargin(gas)
            const txHash = await writeContractAsync({ ...tx, gas: gasLimit })

            addTransaction(
              { hash: txHash },
              {
                type: TransactionType.CLAIM_FARM_REWARDS,
                rewards:
                  earned?.map((item) => ({
                    token: item.token?.symbol || '',
                    amount: item.amount?.quotient.toString() || '0',
                  })) || [],
              }
            )

            await waitForTransactionReceipt({ hash: txHash as Hash })
          } catch (error) {
            console.error('Error claiming rewards:', error)
          } finally {
            setIsClaiming(false)
          }
        }}
        disabled={!earned || earned.length === 0 || isLoading || isClaiming}
      >
        <Text fontSize={20} fontWeight={500}>
          <>Claim</>
        </Text>
      </ButtonPrimary>
    </>
  )
}

export default EarnedCard
